import * as React from "react"
import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { ArrowLeft, ChevronRight, Sparkles } from "lucide-react"
import { PageHeader } from "@/components/PageHeader"
import { ShellFor } from "@/components/generations/ShellFor"
import { SOURCE_KIND_LABELS, useAllowanceNotice } from "@/components/generations/format"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Radio, RadioGroup } from "@/components/ui/radio"
import { Select } from "@/components/ui/select"
import { ProgramSelect, type ProgramOption } from "@/components/ProgramSelect"
import { useFormat } from "@/lib/format"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"
import type { Allowance, GenerationKind, GenerationSummary, SourceSummary } from "@/types/generations"
import type { Difficulty } from "@/types/quizzes"

type SourceMode = "text" | "url" | "file" | "saved"

const DIFFICULTY_LABELS: Record<Difficulty, { id: string }> = {
  easier: { id: "generations.easier" },
  standard: { id: "generations.standard" },
  harder: { id: "generations.harder" },
}

export default function GenerationNew({
  kind,
  official,
  sources,
  selected_source_id,
  sizes,
  difficulties,
  programs,
  exam_versions,
  allowance,
  configured,
  drafts,
}: {
  kind: GenerationKind
  official: boolean
  sources: SourceSummary[]
  selected_source_id: number | null
  sizes: number[]
  difficulties: Difficulty[]
  programs: ProgramOption[]
  exam_versions: { id: number; name: string }[]
  allowance: Allowance
  configured: boolean
  drafts: GenerationSummary[]
}) {
  const intl = useIntl()
  const { wordCount } = useFormat()
  const { props } = usePage<PageProps>()
  const isDeck = kind === "deck"
  const allowanceNotice = useAllowanceNotice()
  const title = official
    ? isDeck ? intl.formatMessage({ id: "generations.title_official_deck" }) : intl.formatMessage({ id: "generations.title_official_quiz" })
    : isDeck ? intl.formatMessage({ id: "generations.title_deck" }) : intl.formatMessage({ id: "generations.title_quiz" })
  const description = isDeck ? intl.formatMessage({ id: "generations.meta_deck" }) : intl.formatMessage({ id: "generations.meta_quiz" })
  const backHref = official ? `/admin/${isDeck ? "decks" : "quizzes"}` : isDeck ? "/flashcards" : "/quizzes"
  const backLabel = official
    ? isDeck ? intl.formatMessage({ id: "generations.back_official_decks" }) : intl.formatMessage({ id: "generations.back_official_quizzes" })
    : isDeck ? intl.formatMessage({ id: "generations.back_flashcards" }) : intl.formatMessage({ id: "generations.back_quizzes" })

  const form = useForm<{
    kind: GenerationKind
    official: string
    source_mode: SourceMode
    text: string
    source_title: string
    url: string
    file: File | null
    source_id: string
    count: string
    difficulty: Difficulty
    exam_version_id: string
    program_id: string
  }>({
    kind,
    official: official ? "1" : "",
    source_mode: selected_source_id ? "saved" : "text",
    text: "",
    source_title: "",
    url: "",
    file: null,
    source_id: selected_source_id ? String(selected_source_id) : sources[0] ? String(sources[0].id) : "",
    count: String(sizes[1] ?? sizes[0]),
    difficulty: "standard",
    exam_version_id: exam_versions[0] ? String(exam_versions[0].id) : "",
    program_id: programs[0] ? String(programs[0].id) : "",
  })

  const blocked = !configured || (!allowance.unlimited && allowance.remaining === 0)
  const notice = allowanceNotice(allowance)
  const modes: { mode: SourceMode; label: string }[] = [
    { mode: "text", label: intl.formatMessage({ id: "generations.mode_text" }) },
    { mode: "url", label: intl.formatMessage({ id: "generations.mode_url" }) },
    { mode: "file", label: intl.formatMessage({ id: "generations.mode_file" }) },
    ...(sources.length > 0 ? [{ mode: "saved" as const, label: intl.formatMessage({ id: "generations.mode_saved" }) }] : []),
  ]

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post("/generations", { forceFormData: true, preserveScroll: true })
  }

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <ShellFor official={official}>
        <Link href={backHref} className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> {backLabel}
        </Link>
        <PageHeader
          title={title}
          description={
            official
              ? isDeck ? intl.formatMessage({ id: "generations.subtitle_official_deck" }) : intl.formatMessage({ id: "generations.subtitle_official_quiz" })
              : isDeck ? intl.formatMessage({ id: "generations.subtitle_deck" }) : intl.formatMessage({ id: "generations.subtitle_quiz" })
          }
        />

        {!configured && (
          <div className="callout callout-danger mt-6">
            <p>{intl.formatMessage({ id: "generations.not_configured" })}</p>
          </div>
        )}
        {configured && notice && (
          <div className={cn("callout mt-6", allowance.remaining === 0 ? "callout-danger" : "callout-signal")}>
            <p>{notice}</p>
          </div>
        )}

        {drafts.length > 0 && (
          <section className="mt-8">
            <h2>{intl.formatMessage({ id: "generations.waiting_heading" })}</h2>
            <ul className="mt-4 divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-page">
              {drafts.map((draft) => (
                <li key={draft.id}>
                  <Link
                    href={`/generations/${draft.id}`}
                    className="flex items-center gap-3 px-4 py-3 no-underline hover:bg-surface"
                  >
                    <Sparkles className="h-4 w-4 shrink-0 text-ink-muted" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-ink-display">
                        {draft.title || intl.formatMessage({ id: "generations.from_source" }, { title: draft.source_title })}
                      </div>
                      <div className="truncate text-xs text-ink-muted">
                        {draft.status === "drafted" ? intl.formatMessage({ id: "generations.ready" }) : intl.formatMessage({ id: "generations.drafting" })} ·{" "}
                        {draft.kind === "deck"
                          ? intl.formatMessage({ id: "generations.about_cards" }, { count: draft.requested_count })
                          : intl.formatMessage({ id: "generations.about_questions" }, { count: draft.requested_count })}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <form onSubmit={submit} className="mt-8 space-y-8">
          <fieldset className="space-y-4">
            <legend>{intl.formatMessage({ id: "generations.source_legend" })}</legend>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label={intl.formatMessage({ id: "generations.source_type" })}>
              {modes.map(({ mode, label }) => (
                <button
                  key={mode}
                  type="button"
                  className="toggle-button"
                  aria-pressed={form.data.source_mode === mode}
                  onClick={() => form.setData("source_mode", mode)}
                >
                  {label}
                </button>
              ))}
            </div>

            {form.data.source_mode === "text" && (
              <>
                <div className="space-y-2">
                  <label htmlFor="source-text">{intl.formatMessage({ id: "generations.text_label" })}</label>
                  <textarea
                    id="source-text"
                    className="form-control form-control-textarea min-h-56"
                    placeholder={intl.formatMessage({ id: "generations.text_placeholder" })}
                    value={form.data.text}
                    onChange={(e) => form.setData("text", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="source-title">{intl.formatMessage({ id: "generations.name_label" })}</label>
                  <Input
                    id="source-title"
                    placeholder={intl.formatMessage({ id: "generations.name_placeholder" })}
                    value={form.data.source_title}
                    onChange={(e) => form.setData("source_title", e.target.value)}
                  />
                </div>
              </>
            )}

            {form.data.source_mode === "url" && (
              <div className="space-y-2">
                <label htmlFor="source-url">{intl.formatMessage({ id: "generations.url_label" })}</label>
                <Input
                  id="source-url"
                  type="url"
                  placeholder="https://"
                  value={form.data.url}
                  onChange={(e) => form.setData("url", e.target.value)}
                />
                <p className="text-xs text-ink-muted">{intl.formatMessage({ id: "generations.url_hint" })}</p>
              </div>
            )}

            {form.data.source_mode === "file" && (
              <div className="space-y-2">
                <label htmlFor="source-file">{intl.formatMessage({ id: "generations.file_label" })}</label>
                <input
                  id="source-file"
                  type="file"
                  accept=".pdf,.docx,.txt,.md,.markdown,application/pdf,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="form-control py-1.5"
                  onChange={(e) => form.setData("file", e.target.files?.[0] ?? null)}
                />
                <p className="text-xs text-ink-muted">
                  {intl.formatMessage({ id: "generations.file_hint" })}
                </p>
              </div>
            )}

            {form.data.source_mode === "saved" && (
              <div className="space-y-2">
                <label htmlFor="source-saved">{intl.formatMessage({ id: "generations.mode_saved" })}</label>
                <Select
                  id="source-saved"
                  value={form.data.source_id}
                  onChange={(e) => form.setData("source_id", e.target.value)}
                >
                  {sources.map((source) => (
                    <option key={source.id} value={source.id}>
                      {intl.formatMessage(
                        { id: "generations.saved_option" },
                        {
                          title: source.title,
                          kind: intl.formatMessage(SOURCE_KIND_LABELS[source.kind]).toLowerCase(),
                          words: wordCount(source.word_count),
                        },
                      )}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {props.errors?.source && (
              <div className="callout callout-danger" role="alert">
                <p>{props.errors.source}</p>
              </div>
            )}
          </fieldset>

          <fieldset className="space-y-3">
            <legend>{isDeck ? intl.formatMessage({ id: "generations.count_deck" }) : intl.formatMessage({ id: "generations.count_quiz" })}</legend>
            <RadioGroup orientation="horizontal">
              {sizes.map((size) => (
                <label key={size} className="flex items-center gap-2 font-normal text-ink-body">
                  <Radio
                    name="count"
                    value={size}
                    checked={form.data.count === String(size)}
                    onChange={() => form.setData("count", String(size))}
                  />
                  {isDeck ? intl.formatMessage({ id: "generations.about_n" }, { size }) : size}
                </label>
              ))}
            </RadioGroup>
          </fieldset>

          {kind === "quiz" && (
            <fieldset className="space-y-3">
              <legend>{intl.formatMessage({ id: "generations.difficulty" })}</legend>
              <RadioGroup orientation="horizontal">
                {difficulties.map((difficulty) => (
                  <label key={difficulty} className="flex items-center gap-2 font-normal text-ink-body">
                    <Radio
                      name="difficulty"
                      value={difficulty}
                      checked={form.data.difficulty === difficulty}
                      onChange={() => form.setData("difficulty", difficulty)}
                    />
                    {intl.formatMessage(DIFFICULTY_LABELS[difficulty])}
                  </label>
                ))}
              </RadioGroup>
            </fieldset>
          )}

          {official && (
            <ProgramSelect
              id="program"
              programs={programs}
              value={form.data.program_id}
              onChange={(value) => form.setData("program_id", value)}
            />
          )}

          {official && exam_versions.length > 0 && (
            <div className="space-y-2">
              <label htmlFor="exam-version">{intl.formatMessage({ id: "generations.tag_areas" })}</label>
              <Select
                id="exam-version"
                value={form.data.exam_version_id}
                onChange={(e) => form.setData("exam_version_id", e.target.value)}
              >
                {exam_versions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {version.name}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={blocked || form.processing}>
              <Sparkles className="h-4 w-4" /> {form.processing ? intl.formatMessage({ id: "generations.reading" }) : isDeck ? intl.formatMessage({ id: "generations.generate_deck" }) : intl.formatMessage({ id: "generations.generate_quiz" })}
            </Button>
            {official && <Badge tone="muted">{intl.formatMessage({ id: "generations.unlimited" })}</Badge>}
          </div>
        </form>
      </ShellFor>
    </>
  )
}
